import { addAlert, removeAlert } from '../configTdm';
import {
  loadIssuesForm,
  toSearchParams,
} from '../lib/sharedSearchService';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TimePicker from '../../views/template/timepicker/timepicker';
import TimepickerDisabled from '../../views/util/timepickerDisabled';
import { searchIssuesService } from './issueService';
import _ from 'lodash';
import DateSelector from '../lib/DateSelector/DateSelector';
import { SuggestAirline, SuggestAirport } from '../reference/AutoSuggests';
import { SuggestAirlineProps, SuggestAirportProps } from '../headersegment/interfaces';
import { validateField } from '../lib/filters';

interface AdvForm {
  [key: string]: any;
  type: string;
  state: string;
  assignee: string | number;
  recordLocator: string | null;
  airline: string | null;
  flightNumber: string | null;
  depAirport: string | null;
  groupName: string | null;
  limit: string;
  dateOfTravel: string | null;
  resolveByMin: string | null;
  resolveByMax: string | null;
}

interface TemplateProps {
  isMyIssues: () => boolean;
  isOpenIssues: () => boolean;
  updateTitle: () => void;
  searchApply: () => void;
  resetIssues: () => void;
}

const Template: React.FC<TemplateProps> = ({
  isMyIssues,
  isOpenIssues,
  updateTitle,
  searchApply,
  resetIssues
}) => {
  const [advForm, setAdvForm] = useState<AdvForm>({
    type: '',
    state: '',
    assignee: '',
    recordLocator: null,
    airline: null,
    flightNumber: null,
    depAirport: null,
    groupName: null,
    limit: '',
    dateOfTravel: null,
    resolveByMin: null,
    resolveByMax: null,
  });
  const [resolveByMaxSetDefaultTime, setResolveByMaxSetDefaultTime] = useState<
    boolean
  >(false);
  const [assignees, setAssignees] = useState<{ id: string | number; value: string }[]>([
    { id: 0, value: 'Any Assignee' },
  ]);
  const [dateOfTravelPickerOpened, setDateOfTravelPickerOpened] = useState<boolean>(false);
  const [resolveByMinPickerOpened, setResolveByMinPickerOpened] = useState<boolean>(false);
  const [resolveByMaxPickerOpened, setResolveByMaxPickerOpened] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [validateArr, setValidateArr] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  const invalidCriteriaMessage = 'Search criteria is invalid';
  const resolveByMinSet = useMemo(() => advForm.resolveByMin !== null, [
    advForm.resolveByMin
  ]);
  const [hasNonNullValues, setHasNonNullValues] = useState(false);
  useEffect(() => setHasNonNullValues(Object.values(advForm).some(
    (value) => value !== null && value !== undefined && value !== ""
  )), [advForm]);
  const [fieldTouched, setFieldTouched] = useState({} as any);
  const [errorFields, setErrorFields] = useState([] as String[]);

  let rootScopeListeners: any[] = [];

  const resolveByMaxSet = useMemo(() => advForm.resolveByMax !== null, [
    advForm.resolveByMax,
  ]);

  let typeTypes = [
    { id: 0, name: 'Any Type', value: '' },
    { id: 1, name: 'Cancellation', value: 'cancellation' },
    { id: 2, name: 'Diverted', value: 'diverted' },
    { id: 3, name: 'Invalid Itinerary', value: 'invalid_itinerary' },
    { id: 4, name: 'Late Arrival', value: 'late_arrival' },
    { id: 5, name: 'Late Departure', value: 'late_departure' },
    { id: 6, name: 'Connection Issue', value: 'threatened_connection' },
    { id: 7, name: 'Unknown', value: 'generic' },
  ];

  let stateTypes = [
    { id: 0, name: 'Any Status', value: '' },
    { id: 1, name: 'Open', value: 'open' },
    { id: 2, name: 'Closed', value: 'closed' },
    { id: 3, name: 'Assisting', value: 'assisting' },
  ];

  useEffect(() => {
    assignee();
    const searchParams = new URLSearchParams(window.location.search);
    setSubmitDisabled(!(searchParams.size > 0));
    if (resolveByMaxSetDefaultTime && advForm.resolveByMax) {
      const updatedDate = new Date(advForm.resolveByMax);
      updatedDate.setHours(23);
      updatedDate.setMinutes(59);
      updatedDate.setSeconds(59);

      setAdvForm(prev => ({
        ...prev,
        resolveByMax: updatedDate.toISOString(),
      }));

      setResolveByMaxSetDefaultTime(false);
    }
    return () => {
      if (rootScopeListeners && Array.isArray(rootScopeListeners)) {
        rootScopeListeners.forEach(unsubscribe => {
          if (unsubscribe) unsubscribe();
        });
        rootScopeListeners = [];
      }

      typeTypes = [];
      stateTypes = [];

      let clearDateobj = {
        open: () => {},
        dateOptions: { formatYear: '', startingDay: 0, showWeeks: false },
        format: ''
      };

      dateOfTravelPicker = clearDateobj;
      resolveByMinPicker = clearDateobj;
      resolveByMaxPicker = clearDateobj;

      setAdvForm({
        type: '',
        state: '',
        assignee: '',
        recordLocator: null,
        airline: null,
        flightNumber: null,
        depAirport: null,
        groupName: null,
        limit: '',
        dateOfTravel: null,
        resolveByMin: null,
        resolveByMax: null,
      });
      setAssignees([]);
    };
  }, []);

  useEffect(() => {
    if (errorFields && errorFields.length) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [errorFields]);

  const assignee = () => {
    updateTitle();
    if (!isMyIssues() && !isOpenIssues()) {
      searchIssuesService
        .assignees()
        .then(resp => {
          if (resp?.status === 200 && resp?.data) {
            setAssignees([{ id: 0, value: 'Any Assignee' }, ...resp.data]);
          }
        })
        .catch(() => {
          setAssignees([{ id: 0, value: 'Any Assignee' }]);
        })
        .finally(() => {
          const searchParams = new URLSearchParams(window.location.search);
          const paramsObject = Object.fromEntries(searchParams.entries());
          let getSelectedData: { advForm: any, resolveByMaxSetDefaultTime: boolean } = loadIssuesForm(paramsObject);
          setAdvForm(getSelectedData.advForm);
          setResolveByMaxSetDefaultTime(getSelectedData.resolveByMaxSetDefaultTime);
        });
    }
    searchApply();
  }

  const refreshSearchIssues = (params: string) => {
    const currentPath = location.pathname + location.search;
    const targetPath = `/issues/search?${params}`;

    if (currentPath === targetPath) {
      assignee();
    } else {
      navigate(targetPath);
      assignee();
    }
  };

  const submitAdv = useCallback(
    (isValid: boolean) => {
      if (isValid) {
        addAlert(invalidCriteriaMessage);
        return;
      }

      const params = toSearchParams(advForm);
      if (params.length) {
        rootScopeListeners.push(refreshSearchIssues(params.join('&')));
      }
    },
    [advForm],
  );

  const closeAllOpens = () => {
    setDateOfTravelPickerOpened(false);
    setResolveByMinPickerOpened(false);
    setResolveByMaxPickerOpened(false);
  };

  const datePickerOptions = {
    formatYear: 'yy',
    startingDay: 0,
    showWeeks: false,
  };

  let dateOfTravelPicker = {
    open: function(event: React.MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      closeAllOpens();
      setDateOfTravelPickerOpened(true);
    },
    dateOptions: datePickerOptions,
    format: 'dd-MMM-yyyy',
  };

  let resolveByMinPicker = {
    open: function(event: React.MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      closeAllOpens();
      setResolveByMinPickerOpened(true);
    },
    dateOptions: datePickerOptions,
    format: 'dd-MMM-yyyy',
  };

  let resolveByMaxPicker = {
    open: function(event: React.MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      closeAllOpens();
      setResolveByMaxPickerOpened(true);
    },
    dateOptions: datePickerOptions,
    format: 'dd-MMM-yyyy',
  };

  const formStateUpdate = (keyName: string, value: any, type: string, e?: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched({...fieldTouched, [keyName]: true });
    if (keyName === 'assignee' && value === '0') {
      value = null;
    }
    const hasError = validateField(type, value, e);
    if (hasError) {
      setErrorFields([...errorFields, keyName]);
    } else {
      const newErrorFields = errorFields.filter((field) => field !== keyName);
      setErrorFields(newErrorFields);
    }
    setAdvForm({ ...advForm, [keyName]: value });
  };

  const reset = useCallback(() => {
    setAdvForm({
      type: '',
      state: '',
      assignee: '',
      recordLocator: null,
      airline: null,
      flightNumber: null,
      depAirport: null,
      groupName: null,
      limit: '',
      dateOfTravel: null,
      resolveByMin: null,
      resolveByMax: null,
    });
    setFieldTouched({});
    setErrorFields([]);
    setResolveByMaxSetDefaultTime(true);
    setSubmitDisabled(true);

    if (removeAlert) {
      removeAlert(invalidCriteriaMessage);
    }
    resetIssues();
    navigate({
      pathname: location.pathname,
    });
  }, [history, location.pathname]);

  return (
    <>
      <div className="panel-body">
        <form
          name="searchform"
          role="form"
          noValidate
        >
          <div className="row">
            <div className="col-sm-6 col-md-3 col-lg-2">
              <label htmlFor="type" className="db-text">
                Type
              </label>
              <select
                id="type"
                className="form-control btn btn-default dropdown-toggle"
                value={advForm.type}
                onChange={e =>
                  formStateUpdate('type', e.target.value, 'selectString')
                }
              >
                {typeTypes.map(item => (
                  <option key={item.id} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-6 col-md-2 col-lg-2">
              <label htmlFor="status" className="db-text">
                Status
              </label>
              <select
                id="status"
                className="form-control btn btn-default dropdown-toggle"
                value={advForm.state}
                onChange={e =>
                  formStateUpdate('state', e.target.value, 'selectString')
                }
              >
                {stateTypes.map(item => (
                  <option key={item.id} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-6 col-md-2 col-lg-2">
              <label htmlFor="assignee" className="db-text">
                Assignee
              </label>
              <select
                id="assignee"
                className="form-control btn btn-default dropdown-toggle"
                value={advForm.assignee || 0}
                onChange={e =>
                  formStateUpdate(
                    'assignee',
                    e.target.value,
                    'selectString',
                  )
                }
              >
                {assignees.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-6 col-md-2 col-lg-2">
              <DateSelector
                key={`date-of-travel`}
                label="Date of Travel"
                value={advForm.dateOfTravel}
                dateFormat={dateOfTravelPicker.format}
                onChange={(value: any) => {
                  formStateUpdate('dateOfTravel', value, 'date');
                }}
              />
            </div>
            <div className="col-sm-6 col-md-6 col-lg-2">
              <label htmlFor="record-locator" className="db-text">
                Record Locator
              </label>
              <input
                id="record-locator"
                type="text"
                className={`form-control ${errorFields.includes('recordLocator') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['recordLocator'] ? 'ng-dirty' : ''}`}
                value={advForm.recordLocator || ''}
                onChange={e =>
                  formStateUpdate('recordLocator', e.target.value, 'text', e)
                }
                minLength={5}
                maxLength={50}
                placeholder="ex. A1B2C3"
              />
            </div>
            <div className="col-sm-6 col-md-6 col-lg-2">
              <label htmlFor="group-name" className="db-text">
                Trip Group Name
              </label>
              <input
                id="group-name"
                type="text"
                className={`form-control ${errorFields.includes('groupName') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['groupName'] ? 'ng-dirty' : ''}`}
                value={advForm.groupName || ''}
                onChange={e =>
                  formStateUpdate('groupName', e.target.value, 'text', e)
                }
              />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-5">
              <div className="col-sm-5 col-md-4 col-lg-5">
                <label htmlFor="airline" className="db-text">
                  Airline
                </label>
                <SuggestAirline value={advForm.airline || ''} className={`${errorFields.includes('airline') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['airline'] ? 'ng-dirty' : ''}`} callback={(value: SuggestAirlineProps) => {
                  formStateUpdate('airline', value, 'select');
                }}></SuggestAirline>
              </div>
              <div className="col-sm-2 col-md-2 col-lg-2">
                <label htmlFor="flight-number" className="db-text">
                  Flight #
                </label>
                <input
  id="flight-number"
  type="number"
  className={`form-control ${errorFields.includes('flightNumber') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['flightNumber'] ? 'ng-dirty' : ''}`}
  value={advForm.flightNumber || ''}
  onChange={e =>
    formStateUpdate('flightNumber', e.target.value, 'number', e)
  }
  onKeyDown={(e) => {
    if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
      e.preventDefault();
    }
  }}
  maxLength={4}
/>
              </div>
              <div className="col-sm-5 col-md-6 col-lg-5">
                <label htmlFor="airport" className="db-text">
                  Airport
                </label>
                <SuggestAirport id='airport' value={advForm.depAirport || ''} className={`${errorFields.includes('depAirport') ? 'ng-invalid' : 'ng-valid'} ${fieldTouched['depAirport'] ? 'ng-dirty' : ''}`} callback={(value: SuggestAirportProps) => {
                  formStateUpdate('depAirport', value, 'select');
                }}></SuggestAirport>
              </div>
            </div>
            <div className="col-sm-12 col-md-11 col-lg-7">
              <div className="row">
                <div className="col-xs-6 col-sm-6 col-md-3 resolve-by">
                  <DateSelector
                    key={`min-resolve-by`}
                    label="Minimum Resolve By"
                    value={advForm.resolveByMin}
                    dateFormat={resolveByMinPicker.format}
                    onChange={(value: any) => {
                      formStateUpdate('resolveByMin', value, 'date');
                    }}
                  />
                </div>
                <div className="col-xs-6 col-sm-6 col-md-3 timepicker">
                  <p className="input-group"></p>
                  {resolveByMinSet ? (
                    <div>
                      <TimePicker selectedTime={advForm.resolveByMin} onChange={(time) => {
                        formStateUpdate('resolveByMin', time.toString(), 'time');
                      }}/>
                    </div>
                  ) : (
                    <div>
                      <TimepickerDisabled />
                    </div>
                  )}
                </div>
                <div className="col-xs-6 col-sm-6 col-md-3 resolve-by">
                  <DateSelector
                    key={`max-resolve-by`}
                    label="Maximum Resolve By"
                    value={advForm.resolveByMax}
                    dateFormat={resolveByMaxPicker.format}
                    onChange={(value: any) => {
                      formStateUpdate('resolveByMax', value.setHours(23,59,59,0), 'date');
                    }}
                  />
                </div>
                <div className="col-xs-6 col-sm-6 col-md-3 timepicker pull-right">
                  <p className="input-group"></p>
                  {resolveByMaxSet ? (
                    <div>
                      <TimePicker selectedTime={advForm.resolveByMax} onChange={(time) => {
                        formStateUpdate('resolveByMax', time.toString(), 'time');
                      }}/>
                    </div>
                  ) : (
                    <div>
                      <TimepickerDisabled />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row form-group-sep">
            <div className="col-md-8"></div>
            <div className="col-md-4 search-button">
              <div className="form-group">
                <button
                  className="btn btn-default button-clear"
                  type="button"
                  onClick={reset}
                  disabled={!hasNonNullValues}
                >CLEAR</button>
                <button
                  className="btn btn-default button-action"
                  type="button"
                  onClick={() => submitAdv(submitDisabled)}
                  disabled={!hasNonNullValues}
                >SEARCH</button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default Template;
